import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RestService } from 'app/shared/rest.service';
import { ItineraryService } from 'app/shared/itinerary.service';
import { NotificationService } from 'app/shared/notifications/notification.service';
import { UserService } from 'app/pages/com.usblick.common/auth/user.service';
import { ProductFactoryService } from 'services/itinerary/products/product-factory.service';
import { IProductCreationParams } from 'model/product.interface';
import { FlySearchPayload, FlightSearchLeg, FlightOffer, FlightSegment } from 'model/fly.interface';
import { keyRandomUnique } from 'utils/helpers';
import { HttpErrorResponse } from '@angular/common/http';
import { tagFlightBadges } from 'app/content-result/category-result/filters-model';

interface SelectedLeg {
  legIndex: number;
  label: string;
  offer: any;
}

const ORDINAL_TRAMO_LABELS = ['Tramo 1', 'Tramo 2', 'Tramo 3', 'Tramo 4', 'Tramo 5'];

@Component({
  selector: 'usb-flight-legs-search',
  templateUrl: './flight-legs-search.component.html',
  styleUrls: ['./flight-legs-search.component.scss']
})
export class FlightLegsSearchComponent implements OnInit {

  private readonly CATEGORY_ID = 5;

  @Input() payload: any;
  @Input() arrayResults: any;
  @Input() currencies: any[];
  @Input() currencyAgency: any;
  @Input() clientsSettingAppBooking: any;
  @Input() paymentAgencyActive: any;
  @Input() qtyProductValid: number;

  @Output() productAdded = new EventEmitter<any>();

  legs: FlightSearchLeg[] = [];
  legLabels: string[] = [];
  activeLegIndex = 0;

  legResults: { [index: number]: FlightOffer[] } = {};
  loadingLeg = false;
  legError = false;

  selectedLegs: { [index: number]: SelectedLeg } = {};
  finalizing = false;
  finalized = false;

  private searchPayload: FlySearchPayload;
  private flightGroupId = String(keyRandomUnique());

  constructor(
    private restService: RestService,
    private itineraryService: ItineraryService,
    private notificationService: NotificationService,
    private userService: UserService,
    private productFactory: ProductFactoryService,
  ) { }

  ngOnInit(): void {
    this.searchPayload = Array.isArray(this.payload) ? this.payload[0] : this.payload;
    this.legs = this.searchPayload?.legs || [];
    this.legLabels = this.buildLegLabels(this.legs);

    if (this.legs.length > 0) {
      this.updateActiveLegPayload();
      this.searchLeg(0);
    }
  }

  // "Ida"/"Vuelta" sólo cuando son exactamente 2 tramos y el segundo es el
  // reverso exacto del primero — cualquier otro caso (multidestino) usa
  // "Tramo 1", "Tramo 2", ...
  private buildLegLabels(legs: FlightSearchLeg[]): string[] {
    if (legs.length === 2 && legs[1].origin === legs[0].destination && legs[1].destination === legs[0].origin) {
      return ['Salida', 'Regreso'];
    }
    return legs.map((_leg, index) => ORDINAL_TRAMO_LABELS[index] || `Tramo ${index + 1}`);
  }

  get activeLegLabel(): string {
    return this.legLabels[this.activeLegIndex] || '';
  }

  get offersForActiveLeg(): FlightOffer[] {
    return this.legResults[this.activeLegIndex] || [];
  }

  // payload de un solo tramo, en el mismo formato (array) que ya recibe
  // usb-base-result para el resto de las categorías. Se recalcula sólo
  // cuando cambia el tramo activo (no es un getter): usb-base-result lee
  // este input en su propio ngOnChanges, y una referencia nueva en cada
  // ciclo de detección de cambios lo dispararía sin necesidad todo el rato.
  activeLegPayload: any[] = [];

  private updateActiveLegPayload(): void {
    this.activeLegPayload = [{
      ...this.searchPayload,
      legs: [this.legs[this.activeLegIndex]],
    }];
  }

  isLegSelected(index: number): boolean {
    return !!this.selectedLegs[index];
  }

  canChangeLeg(index: number): boolean {
    return !this.finalized && !this.finalizing && index < this.activeLegIndex;
  }

  changeLeg(index: number): void {
    if (!this.canChangeLeg(index)) {
      return;
    }
    this.activeLegIndex = index;
    this.updateActiveLegPayload();
    if (!this.legResults[index]) {
      this.searchLeg(index);
    }
  }

  private searchLeg(index: number): void {
    const leg = this.legs[index];
    if (!leg) {
      return;
    }

    this.loadingLeg = true;
    this.legError = false;

    const onewayPayload = {
      categoriesId: this.searchPayload?.categoriesId || [this.CATEGORY_ID],
      others: this.searchPayload?.others,
      legs: [leg],
      market: this.searchPayload?.market,
      currency: this.searchPayload?.currency,
      cabinClass: this.searchPayload?.cabinClass,
      airlines: this.searchPayload?.airlines || [],
      client_id: this.searchPayload?.client_id,
    };

    this.restService.doPost('app', 'search', onewayPayload).subscribe({
      next: (response: any) => {
        const flights = response?.results?.[0]?.flights || [];
        // Cada tramo es una búsqueda aparte: sus ofertas no pasaron por el cálculo de
        // category-result, así que se marcan acá (antes de asignarlas, para que la card
        // ya las reciba con isCheapest/isFastest).
        tagFlightBadges(flights);
        this.legResults[index] = flights;
        this.loadingLeg = false;
      },
      error: (error: HttpErrorResponse) => {
        console.error(`Error buscando ${this.legLabels[index]}:`, error);
        this.legError = true;
        this.loadingLeg = false;
      }
    });
  }

  // Llamado desde (flightFareSelectedForLeg) de cada usb-base-result de la
  // lista del tramo activo — el modal de detalle ya aplicó la tarifa elegida
  // sobre la oferta (deferFlightCreation evita que cree el producto solo).
  onLegChosen(selection: { offer: any; fare: any }): void {
    const index = this.activeLegIndex;
    this.selectedLegs[index] = { legIndex: index, label: this.legLabels[index], offer: selection.offer };

    const nextIndex = index + 1;
    if (nextIndex < this.legs.length) {
      this.activeLegIndex = nextIndex;
      this.updateActiveLegPayload();
      if (!this.legResults[nextIndex]) {
        this.searchLeg(nextIndex);
      }
      return;
    }

    this.finalizeSelection();
  }

  // ==========================
  // Alta de productos — un item de itinerario por tramo, igual que
  // traslados con ida/vuelta separados.
  // ==========================

  private async finalizeSelection(): Promise<void> {
    this.finalizing = true;
    const loadingToast = this.notificationService.loading('Agregando vuelos...');

    const orderedLegs = Object.keys(this.selectedLegs)
      .map(key => Number(key))
      .sort((a, b) => a - b);

    try {
      const productService = this.productFactory.getProductService(this.CATEGORY_ID);

      for (const legIndex of orderedLegs) {
        const selected = this.selectedLegs[legIndex];
        const legSearchPayload = [{
          ...this.searchPayload,
          legs: [this.legs[legIndex]],
        }];

        const productParams: IProductCreationParams = {
          item: selected.offer,
          product: selected.offer,
          payload: legSearchPayload,
          categoryId: this.CATEGORY_ID,
          othersPerCategory: {
            searchId: this.arrayResults?.searchId,
            userService: this.userService,
            legTag: {
              flightGroupId: this.flightGroupId,
              direction: selected.label === 'Ida' ? 'IDA' : selected.label === 'Vuelta' ? 'VUELTA' : selected.label,
              legIndex,
            },
          },
        };

        const result = productService.createProduct(productParams);
        if (!result) {
          throw new Error(`No se pudo crear el producto del tramo "${selected.label}"`);
        }

        await new Promise<void>((resolve, reject) => {
          this.itineraryService.add(result).subscribe({
            next: (response: any) => {
              if (!response) {
                reject(new Error('No se recibió confirmación del servidor'));
                return;
              }
              resolve();
            },
            error: (error: HttpErrorResponse) => reject(error),
          });
        });

        // Igual que modal-transports: un pequeño respiro entre altas
        // consecutivas para no pisar la respuesta del backend.
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      this.itineraryService.evaluateDateItineraryRange(this.itineraryService.itinerary['items']);
      this.itineraryService.updateItinerary(this.itineraryService.itinerary);

      this.notificationService.close(loadingToast);
      this.notificationService.success('¡Felicitaciones! Vuelos agregados con éxito.');
      this.finalized = true;
      this.finalizing = false;
      this.productAdded.emit();

    } catch (error) {
      this.notificationService.close(loadingToast);
      console.error('Error al agregar los vuelos por tramo:', error);
      this.notificationService.error('Ocurrió un error al agregar los vuelos');
      this.finalizing = false;
    }
  }

  // ==========================
  // Formato para la franja de tramos ya elegidos
  // ==========================

  getFirstSegment(offer: any): FlightSegment {
    return offer?.itinerary?.[0]?.options?.[0]?.segments?.[0];
  }

  getLastSegment(offer: any): FlightSegment {
    const segments = offer?.itinerary?.[0]?.options?.[0]?.segments;
    return segments ? segments[segments.length - 1] : null;
  }

  formatTime(dateTime: string): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true });
  }
}
